import { useState } from 'react';
import { withUnreadDivider } from '../../components/common/chat/timeline/chatUnreadDivider';
import ArtistSidebar from '../../components/artist/ArtistSidebar';
import { formatItemCount } from '../../utils/datetime';
import { ADMIN_MESSAGE_FORMATS } from '../../constants/messageFormats';
import ChatComposer from '../../components/common/chat/composer/ChatComposer';
import ChatQuestionPendingBar from '../../components/common/chat/questions/ChatQuestionPendingBar';
import ChatImageModal from '../../components/common/chat/composer/ChatImageModal';
import ChatProfileModalLayer from '../../components/common/chat/members/ChatProfileModalLayer';
import ChatAnnouncementBar from '../../components/common/chat/announcements/ChatAnnouncementBar';
import ChatAnnouncementListModal from '../../components/common/chat/announcements/ChatAnnouncementListModal';
import ChatMessageReactionBar from '../../components/common/chat/reactions/ChatMessageReactionBar';
import ChatMessageMenuReactionPicker from '../../components/common/chat/reactions/ChatMessageMenuReactionPicker';
import ChatMessageReactionDetailModal from '../../components/common/chat/reactions/ChatMessageReactionDetailModal';
import ChatReplySnippet from '../../components/common/chat/composer/ChatReplySnippet';
import ChatHeaderIconButton from '../../components/common/chat/shared/ChatHeaderIconButton';
import ChatNoteSystemMessageRow from '../../components/common/chat/notes/ChatNoteSystemMessageRow';
import { supportsChatMessageReaction } from '../../components/common/chat/reactions/chatMessageReactionUtils';
import ArtistGroupChatPane from '../../components/artist/chats/ArtistGroupChatPane';
import ChatEmptyState from '../../components/common/chat/shared/ChatEmptyState';
import ChatTargetListRow from '../../components/common/chat/sidepanel/ChatTargetListRow';
import ChatMentionTaskNotice from '../../components/common/chat/mentions/ChatMentionTaskNotice';
import { renderChatSearchHighlight } from '../../components/common/chat/search/chatSearchHighlight';
import ChatMentionTaskResolutionModal from '../../components/common/chat/mentions/ChatMentionTaskResolutionModal';
import ChatHeaderOverflowMenu from '../../components/common/chat/shared/ChatHeaderOverflowMenu';
import ChatLinksModal from '../../components/common/chat/assets/ChatLinksModal';
import ChatMediaModal from '../../components/common/chat/assets/ChatMediaModal';
import { artistPersonalChatAssetSource } from '../../api/chatAssets';
import ChatTargetSidebarLoading from '../../components/common/chat/sidepanel/ChatTargetSidebarLoading';
import ChatTargetSidebarToolbar from '../../components/common/chat/sidepanel/ChatTargetSidebarToolbar';
import ChatTargetSearchModal from '../../components/common/chat/search/ChatTargetSearchModal';
import {
  ArtistTacticartChatGuidanceMessage,
  ChatAnnouncementAvatar,
} from '../../components/common/chat/timeline/ArtistChatGuidanceSystemMessages';
import ChatWorkspaceLayout from '../../components/common/chat/shared/ChatWorkspaceLayout';
import ChatAreaHeader from '../../components/common/chat/shared/ChatAreaHeader';
import ChatSidePanelToggleButton from '../../components/common/chat/sidepanel/ChatSidePanelToggleButton';
import ChatSidePanelHeader from '../../components/common/chat/sidepanel/ChatSidePanelHeader';
import AnnouncementSummarySidePanelCard from '../../components/common/chat/sidepanel/AnnouncementSummarySidePanelCard';
import ChatSidePanelCard from '../../components/common/chat/sidepanel/ChatSidePanelCard';
import ChatSidePanelPeopleList from '../../components/common/chat/members/ChatSidePanelPeopleList';
import ChatSidePanelSection from '../../components/common/chat/sidepanel/ChatSidePanelSection';
import IndividualStandardMessageRow from '../../components/common/chat/timeline/IndividualStandardMessageRow';
import AdminSenderName from '../../components/common/chat/timeline/AdminSenderBadge';
import ArtistPersonalNotesPanel from '../../components/artist/chats/ArtistPersonalNotesPanel';
import {
  getSystemAvatarIconClass,
  getSystemAvatarToneClass,
  getSystemToneClass,
} from '../../components/common/chat/timeline/systemMessageUtils';
import { appendVariant } from '../../components/common/chat/composer/chatAssetUtils';
import { renderIndividualStandardMessageContent, resolveIndexedImagePreview } from '../../components/common/chat/timeline/individualMessageContentRenderer';
import { renderIndividualSystemMessage } from '../../components/common/chat/timeline/individualSystemMessageRenderer';
import { shouldShowAdminSenderForMessage } from '../../components/common/chat/timeline/adminSenderExclusionUtils';
import { isGroupNoteMessage } from '../../components/common/chat/notes/groupNoteMessageUtils';
import { renderAdminSenderMeta } from '../../components/admin/chats/chatPageParts';
import { buildArtistIndividualMessagePresentation } from '../../components/common/chat/timeline/chatMessagePresentationUtils';
import ChatDragOverlay from '../../components/common/chat/composer/ChatDragOverlay';
import { formatDateTimeJST, formatDateJPFromAny, toYmdSlash, toHHmm, sidebarTimeLabelJST } from '../../utils/datetime';
import DsSelect from '../../components/common/DsSelect';
import PersonAvatar from '../../components/common/avatar/PersonAvatar';
import ExplanationButton from '../../components/common/ExplanationButton';
import { buildArtistMessageFileDownloadUrl } from './useArtistChatPageElement.helpers';

const ARTIST_PERSONAL_ASSET_SOURCE = artistPersonalChatAssetSource();

const CHAT_ABOUT_NOTICE_STORAGE_KEY = 'casticart:artist-chat-about-notice-dismissed';

export default function ArtistChatPageView({ context }) {
  const [showLinksModal, setShowLinksModal] = useState(false);
  const [showMediaModal, setShowMediaModal] = useState(false);
  const [chatAboutNoticeDismissed, setChatAboutNoticeDismissed] = useState(() => {
    try {
      return window.localStorage.getItem(CHAT_ABOUT_NOTICE_STORAGE_KEY) === '1';
    } catch {
      return false;
    }
  });
  const dismissChatAboutNotice = () => {
    setChatAboutNoticeDismissed(true);
    try {
      window.localStorage.setItem(CHAT_ABOUT_NOTICE_STORAGE_KEY, '1');
    } catch {
      // localStorage が使えない環境では、その場限りの非表示にとどめる。
    }
  };
  const {
    chatTopics, selectedGroupTopic, setSelectedGroupTopic,    ADMIN_PROFILE_IMAGE_PATH,
    ARTIST_TARGET_ROW_CLASSES,
    TACTICART_PERSONAL_CHAT_TARGET_INTRO,
    TACTICART_TARGET_ICON_PATH,
    activeAnchorMessageId,
    activeChatTarget,
    adminParticipants,
    announcementActionMessageId,
    announcementCount,
    announcementSummaries,
    announcements,
    applyTargetSearchModal,
    artistGroupDbg,
    artistGroupUnreadMap,
    artistPersonalMessageCount,
    artistUser,
    buildAssetUrl,
    buildFlyerUrl,
    buildGroupChatIconUrl,
    buildGroupThreadPreviewText,
    buildMessageImageUrl,
    canAnnouncementMessage,
    canCopyMessage,
    canReactMessage,
    canReplyMessage,
    canUnsendMessage,
    chatContainerRef,
    chatWrapRef,
    clearMessageLongPress,
    clearTargetSearchModal,
    clearToolbarSearch,
    closeMessageMenu,
    closeModal,
    closeProfileModal,
    closeReactionDetail,
    containerMobileClass,
    currentImageIndex,
    displayMessages,
    unreadDividerMessageId,
    extractFirstUrl,
    fetchInitialMessages,
    filteredGroupThreads,
    formatMessageContent,
    getAdminDisplayName,
    getCachedFlyerUrl,
    getHoldDeclineLink,
    goNewer,
    goOlder,
    groupThreads,
    handleBannerClick,
    handleCopyMessage,
    handleDownload,
    handleGroupAccessLost,
    handleGroupThreadsRefresh,
    handleImageClick,
    handleJumpToReplyMessage,
    handleMentionClick,
    handleMessageContextMenu,
    handleMessageMenuReactionSelect,
    handleMessageTouchStart,
    handleMobileBackToTargetList,
    handleOpenInlinePartialCopy,
    handlePersonalMentionQueryChange,
    handleSelectAnnouncementMessage,
    handleSelectReplyMessage,
    handleToggleAnnouncementMessage,
    handleTouchEnd,
    handleTouchStart,
    handleUnsendMessage,
    handleUploading,
    hasMore,
    imageFullUrls,
    imageIndexByMessageId,
    imageMessages,
    imageModalItems,
    imagePreviewUrls,
    inlineCopyMessageId,
    isAnnouncedMessage,
    isAnnouncementListOpen,
    isDragActive,
    isGroupTarget,
    isLoadingGroupThreads,
    isMobile,
    isModalOpen,
    isPersonalTargetActive,
    isReactionDetailOpen,
    isReactionPending,
    isReadOnly,
    isSidePanelOpen,
    isSystemFormat,
    isTargetSearchMode,
    isTargetSelectionPending,
    isUploading,
    latestAnnouncement,
    loadingMentionResolutionTargetKey,
    loadingOlderMessages,
    mentionResolutionModalProps,
    messageFilter,
    messageMenu,
    messageMenuRef,
    mobileTargetListPrefRef,
    normalizeId,
    normalizedMessageFilter,
    noteCount,
    openAdminProfile,
    openMentionResolution,
    openReactionDetail,
    openTargetSearchModal,
    overlayRect,
    ownArtistId,
    parseReminderContent,
    pendingGroupMentionTargets,
    pendingMentionTargets,
    personalMentionCandidates,
    personalTargetRows,
    pickFlyerFilename,
    profileTarget,
    quickTaskInitialMentionDraft,
    reactionDetailGroups,
    reactionDetailLoading,
    refreshNoteCount,
    refreshPendingMentionTargets,
    renderAdminAvatar,
    renderPartialCopyContent,
    replyDraft,
    resolveGroupMentionTarget,
    resolveMentionTarget,
    resolvePrimaryGenreObj,
    resolvingMentionTargetId,
    scrollToLatest,
    searchDraft,
    searchSummaryLabel,
    searchToolbarChips,
    selectedGroupAnchorMessageId,
    selectedGroupThreadId,
    setActiveChatTarget,
    setIsAnnouncementListOpen,
    setIsSidePanelOpen,
    setMobileView,
    setReplyDraft,
    setSearchDraft,
    setSelectedGroupAnchorMessageId,
    setSelectedGroupThreadId,
    setSelectedPersonalAnchorMessageId,
    setShowNotes,
    setShowSearchModal,
    shouldShowChatGuidance,
    showDelayedInitialLoading,
    showMobileTargetBackButton,
    showNewMsgBanner,
    showNotes,
    showPersonalTargetRow,
    showSearchModal,
    showTargetSidebar,
    toastError,
    toggleReaction,
    unpinAnnouncement,
    unreadCount,
    updateGroupThreadMeta,
  } = context;

  return (<div className="artist-dashboard">
      <ArtistSidebar />
      <div className={`artist-message-container${containerMobileClass}`}>
        {/* D&D監視対象：chat-dnd-scope を付与 */}
        <ChatWorkspaceLayout
          className={`chat-main-with-side${isGroupTarget ? ' chat-main-with-side--group' : ''}${
            !isGroupTarget && !isSidePanelOpen ? ' is-side-panel-collapsed' : ''
          }`}
        >
        {showTargetSidebar && (
          <aside className="artist-chat-target-sidebar chat-target-sidebar">
            <ChatTargetSidebarToolbar
              onOpenSearch={openTargetSearchModal}
              summary={searchSummaryLabel}
              chips={searchToolbarChips}
            />
            <ul
              className="artist-chat-target-sidebar__list"
              aria-busy={isLoadingGroupThreads ? 'true' : 'false'}
            >
              {isTargetSearchMode ? (
                <li className="event-list__result-header">
                  <span className="event-list__result-count">
                    <i className="fa-solid fa-magnifying-glass" aria-hidden="true" />
                    検索結果 {formatItemCount(personalTargetRows.length + filteredGroupThreads.length)}
                  </span>
                  <button
                    type="button"
                    className="event-list__result-clear"
                    onClick={clearToolbarSearch}
                  >
                    <i className="fa-solid fa-xmark" aria-hidden="true" />
                    <span>解除</span>
                  </button>
                </li>
              ) : null}
              {showPersonalTargetRow
                ? personalTargetRows.map((row) => {
                    const personalUnreadCount = isPersonalTargetActive && chatTopics.canClearParentUnread()
                      ? 0
                      : Math.max(
                          Number(unreadCount || 0),
                          Number(artistPersonalMessageCount || 0)
                        );
                    // 検索/絞り込み中は受信箱の未対応メンションCTAを結果に混ぜない
                    const mentionTask = isTargetSearchMode
                      ? null
                      : pendingMentionTargets[ownArtistId] || null;
                    const isMessageHit =
                      !!normalizedMessageFilter && !!String(row?.searchHitMessageId || '').trim();
                    return (
                    <ChatTargetListRow
                      topicScope="individual" topicConversationId={ownArtistId} hasTopics={chatTopics.rows.length > 0}
                      onTopicSelect={(topic) => { chatTopics.selectTopic(topic); setSelectedGroupThreadId(''); setActiveChatTarget('personal'); if (isMobile) setMobileView('chat'); }}
                      key={row.key}
                      classNames={ARTIST_TARGET_ROW_CLASSES}
                      className={isPersonalTargetActive ? 'is-active' : ''}
                      onClick={() => {
                        const hitMessageId = String(row?.searchHitMessageId || '').trim();
                        setSelectedPersonalAnchorMessageId(hitMessageId);
                        setSelectedGroupAnchorMessageId('');
                        setSelectedGroupThreadId('');
                        setActiveChatTarget('personal');
                        fetchInitialMessages(true, { anchorMessageId: hitMessageId }).catch(() => {});
                        if (isMobile) {
                          mobileTargetListPrefRef.current = false;
                          setMobileView('chat');
                        }
                      }}
                      avatar={
                        <span
                          className="artist-chat-target-sidebar__avatar chat-target-row__avatar"
                          style={{
                            backgroundImage: `url(${TACTICART_TARGET_ICON_PATH})`,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                            backgroundRepeat: 'no-repeat',
                          }}
                        />
                      }
                      title="タクティカート"
                      preview={isMessageHit || row.showIntro ? undefined : row.preview}
                      previewNode={
                        isMessageHit ? (
                          <div className="artist-last-preview">
                            {renderChatSearchHighlight(row.preview, messageFilter)}
                          </div>
                        ) : undefined
                      }
                      belowContent={
                        row.showIntro || mentionTask?.count > 0 ? (
                          <>
                            {row.showIntro ? (
                              <div className="artist-chat-target-sidebar__intro">
                                <i
                                  className="fa-solid fa-circle-info artist-chat-target-sidebar__intro-icon"
                                  aria-hidden="true"
                                />
                                <span className="artist-chat-target-sidebar__intro-text">
                                  {TACTICART_PERSONAL_CHAT_TARGET_INTRO}
                                </span>
                              </div>
                            ) : null}
                            {mentionTask?.count > 0 ? (
                              <ChatMentionTaskNotice
                                count={mentionTask.count}
                                disabled={
                                  resolvingMentionTargetId === ownArtistId ||
                                  loadingMentionResolutionTargetKey === `personal:${ownArtistId}`
                                }
                                onResolve={() =>
                                  openMentionResolution({
                                    scope: 'personal',
                                    targetId: ownArtistId,
                                    count: mentionTask.count,
                                    title: 'タクティカートの未対応メンション',
                                    resolveTarget: resolveMentionTarget,
                                  })
                                }
                                onOpen={() => {
                                  const messageId = String(mentionTask.messageId || '').trim();
                                  if (!messageId) return;
                                  setSelectedPersonalAnchorMessageId(messageId);
                                  setSelectedGroupAnchorMessageId('');
                                  setSelectedGroupThreadId('');
                                  setActiveChatTarget('personal');
                                  fetchInitialMessages(true, { anchorMessageId: messageId }).catch(() => {});
                                  if (isMobile) {
                                    mobileTargetListPrefRef.current = false;
                                    setMobileView('chat');
                                  }
                                }}
                              />
                            ) : null}
                          </>
                        ) : null
                      }
                      time={row.createdAt ? sidebarTimeLabelJST(row.createdAt) : null}
                      badge={
                        !isMessageHit && personalUnreadCount > 0
                          ? (personalUnreadCount > 99 ? '99+' : personalUnreadCount)
                          : null
                      }
                    />
                  );
                })
                : null}
              <li className="artist-chat-target-sidebar__section" aria-hidden="true">
                グループチャット
              </li>
              {isLoadingGroupThreads ? (
                <ChatTargetSidebarLoading variant="row" count={4} />
              ) : (
                filteredGroupThreads.map((thread) => {
                  const iconUrl = buildGroupChatIconUrl(thread.icon_file, 'proxy');
                  const rowActive =
                    isGroupTarget && normalizeId(thread.thread_id) === normalizeId(selectedGroupThreadId);
                  const rowClosed = String(thread.status || '') !== 'open';
                  const threadUnreadCount = Number(
                    artistGroupUnreadMap?.[thread.thread_id] ?? thread.unread_count ?? 0
                  );
                  const unreadCount = threadUnreadCount;
                  // 検索/絞り込み中は受信箱の未対応メンションCTAを結果に混ぜない
                  const mentionTask = isTargetSearchMode
                    ? null
                    : pendingGroupMentionTargets[normalizeId(thread.thread_id)] || null;
                  const isMessageHit =
                    thread.search_result_type === 'message-hit' ||
                    (!!normalizedMessageFilter && !!String(thread.search_hit_message_id || '').trim());
                  return (
                    <ChatTargetListRow
                      topicScope="group" topicConversationId={thread.thread_id} hasTopics={thread.has_topics}
                      onTopicSelect={(topic) => { setSelectedGroupTopic(topic); setSelectedGroupThreadId(thread.thread_id); setActiveChatTarget('group'); if (isMobile) setMobileView('chat'); }}
                      key={
                        thread.search_hit_message_id
                          ? `${thread.thread_id}-${thread.search_hit_message_id}`
                          : thread.thread_id
                      }
                      classNames={ARTIST_TARGET_ROW_CLASSES}
                      className={`${rowActive ? 'is-active' : ''}${
                        rowClosed ? ' is-closed' : ''
                      }`}
                      onClick={() => {
                        setSelectedGroupTopic(null);
                        artistGroupDbg('group-row:select', {
                          threadId: normalizeId(thread?.thread_id) || null,
                          searchHitMessageId:
                            String(thread?.search_hit_message_id || '').trim() || null,
                          previousSelectedGroupThreadId:
                            normalizeId(selectedGroupThreadId) || null,
                          activeChatTarget,
                          isMobile,
                        });
                        setSelectedPersonalAnchorMessageId('');
                        setSelectedGroupAnchorMessageId(
                          String(thread?.search_hit_message_id || '').trim()
                        );
                        setSelectedGroupThreadId(thread.thread_id);
                        setActiveChatTarget('group');
                        if (isMobile) {
                          mobileTargetListPrefRef.current = false;
                          setMobileView('chat');
                        }
                      }}
                      avatar={
                        <span
                          className="artist-chat-target-sidebar__avatar chat-target-row__avatar"
                          style={
                            iconUrl
                              ? {
                                  backgroundImage: `url(${iconUrl})`,
                                  backgroundSize: 'cover',
                                  backgroundPosition: 'center',
                                  backgroundRepeat: 'no-repeat',
                                }
                              : undefined
                          }
                        >
                          {!iconUrl && ((thread.title || 'G').trim().charAt(0) || 'G')}
                        </span>
                      }
                      title={thread.title || 'グループチャット'}
                      preview={isMessageHit ? undefined : buildGroupThreadPreviewText(thread)}
                      previewNode={
                        isMessageHit ? (
                          <div className="artist-last-preview">
                            {renderChatSearchHighlight(buildGroupThreadPreviewText(thread), messageFilter)}
                          </div>
                        ) : undefined
                      }
                      belowContent={
                        mentionTask?.count > 0 ? (
                          <ChatMentionTaskNotice
                            count={mentionTask.count}
                            disabled={
                              resolvingMentionTargetId === normalizeId(thread.thread_id) ||
                              loadingMentionResolutionTargetKey === `group:${normalizeId(thread.thread_id)}`
                            }
                            onResolve={() =>
                              openMentionResolution({
                                scope: 'group',
                                targetId: thread.thread_id,
                                count: mentionTask.count,
                                title: thread.title ? `${thread.title} の未対応メンション` : '未対応メンション',
                                resolveTarget: resolveGroupMentionTarget,
                              })
                            }
                            onOpen={() => {
                              const messageId = String(mentionTask.messageId || '').trim();
                              if (!messageId) return;
                              setSelectedPersonalAnchorMessageId('');
                              setSelectedGroupAnchorMessageId(messageId);
                              setSelectedGroupThreadId(thread.thread_id);
                              setActiveChatTarget('group');
                              if (isMobile) {
                                mobileTargetListPrefRef.current = false;
                                setMobileView('chat');
                              }
                            }}
                          />
                        ) : null
                      }
                      time={
                        thread.latest_message_at || thread.updated_at
                          ? sidebarTimeLabelJST(thread.latest_message_at || thread.updated_at)
                          : null
                      }
                      badge={!isMessageHit && unreadCount > 0 ? (unreadCount > 99 ? '99+' : unreadCount) : null}
                    />
                  );
                })
              )}
              {!isLoadingGroupThreads && !showPersonalTargetRow && filteredGroupThreads.length === 0 ? (
                <li className="artist-chat-target-sidebar__muted">
                  {isTargetSearchMode
                    ? '一致するチャットはありません。条件を変えるか解除してください。'
                    : '該当するチャットがありません。'}
                </li>
              ) : null}
              {!isTargetSearchMode && !chatAboutNoticeDismissed ? (
                <li className="artist-chat-target-sidebar__about">
                  <div className="artist-chat-target-sidebar__about-body">
                    <div className="artist-chat-target-sidebar__about-title">チャットについて</div>
                    <p className="artist-chat-target-sidebar__about-text">
                      アーティスト同士での個人間チャットはできません。必要な場合はタクティカートスタッフへグループ作成の依頼を行なってください。
                    </p>
                  </div>
                  <button
                    type="button"
                    className="artist-chat-target-sidebar__about-dismiss"
                    onClick={dismissChatAboutNotice}
                    aria-label="チャットについての案内を閉じる"
                    title="閉じる"
                  >
                    <i className="fa-solid fa-xmark" aria-hidden="true" />
                  </button>
                </li>
              ) : null}
            </ul>
          </aside>
        )}
        <ChatTargetSearchModal
          isOpen={showSearchModal}
          title="チャット検索"
          onClose={() => setShowSearchModal(false)}
          onApply={applyTargetSearchModal}
          onClear={clearTargetSearchModal}
          summary={searchSummaryLabel}
        >
          <div className="chat-target-search-form">
            <div className="chat-target-search-form__field">
              <label className="chat-target-search-form__label" htmlFor="artist-chat-room-type-filter">
                ルーム種別
              </label>
              <DsSelect
                id="artist-chat-room-type-filter"
                className="chat-target-search-form__select"
                value={searchDraft.roomType}
                onChange={(event) =>
                  setSearchDraft((current) => ({ ...current, roomType: event.target.value }))
                }
              >
                <option value="">すべて</option>
                <option value="personal">タクティカート</option>
                <option value="group">グループチャット</option>
              </DsSelect>
            </div>

            <div className="chat-target-search-form__field">
              <label className="chat-target-search-form__label" htmlFor="artist-chat-room-name-filter">
                ルーム名
              </label>
              <input
                id="artist-chat-room-name-filter"
                className="chat-target-search-form__input"
                type="text"
                value={searchDraft.roomName}
                onChange={(event) =>
                  setSearchDraft((current) => ({ ...current, roomName: event.target.value }))
                }
                placeholder="ルーム名で絞り込み"
              />
            </div>

            <div className="chat-target-search-form__field">
              <label className="chat-target-search-form__label" htmlFor="artist-chat-message-filter">
                チャット本文
              </label>
              <input
                id="artist-chat-message-filter"
                className="chat-target-search-form__input"
                type="text"
                value={searchDraft.message}
                onChange={(event) =>
                  setSearchDraft((current) => ({ ...current, message: event.target.value }))
                }
                placeholder="本文キーワード"
              />
            </div>
          </div>
        </ChatTargetSearchModal>
        {isTargetSelectionPending ? (
          <div className="artist-message-chat-container artist-message-chat-container--empty chat-area">
            <ChatEmptyState ariaLabel="チャットが未選択です" />
          </div>
        ) : isGroupTarget ? (
          <ArtistGroupChatPane
            threadId={selectedGroupThreadId}
            topicSelection={selectedGroupTopic}
            anchorMessageId={selectedGroupAnchorMessageId}
            threadSummary={groupThreads.find((thread) => normalizeId(thread.thread_id) === normalizeId(selectedGroupThreadId)) || null}
            readOnly={isReadOnly}
            onThreadMeta={updateGroupThreadMeta}
            onThreadsRefresh={handleGroupThreadsRefresh}
            onAccessLost={handleGroupAccessLost}
            showMobileBackButton={showMobileTargetBackButton}
            onMobileBack={handleMobileBackToTargetList}
          />
        ) : (
        <>
        <div className="artist-message-chat-container chat-area chat-dnd-scope" ref={chatWrapRef}>
          <ChatAreaHeader
            className="artist-chat-main-header"
            mainClassName="artist-chat-main-header__main"
            showBackButton={showMobileTargetBackButton}
            onBack={handleMobileBackToTargetList}
            backButtonClassName="chat-header-back-btn"
            backAriaLabel="チャット一覧に戻る"
            backTitle="チャット一覧に戻る"
            actionsClassName="artist-chat-main-header__actions chat-header-action-group"
            main={(
              <>
                <div
                  className="artist-chat-main-header__avatar"
                  style={{
                    backgroundImage: `url(${TACTICART_TARGET_ICON_PATH})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                  }}
                  aria-hidden="true"
                />
                <div className="artist-chat-main-header__text">
                  <h3>タクティカート</h3>
                </div>
              </>
            )}
            actions={
              <>
                <ChatHeaderIconButton
                  className="artist-group-chat-pane__memberButton"
                  onClick={() => setShowNotes((current) => !current)}
                  aria-label={showNotes ? 'チャットに戻る' : 'ノート'}
                  title={showNotes ? 'チャットに戻る' : 'ノート'}
                  iconClassName={`fa-solid ${showNotes ? 'fa-comment' : 'fa-note-sticky'}`}
                  badgeCount={showNotes ? 0 : noteCount}
                />
                <ChatHeaderOverflowMenu
                  items={[
                    { key: 'links', icon: 'fa-link', label: 'リンク', onClick: () => setShowLinksModal(true) },
                    { key: 'media', icon: 'fa-photo-film', label: '写真・ファイル', onClick: () => setShowMediaModal(true) },
                  ]}
                />
              </>
            }
          />
          <ChatQuestionPendingBar />

          {!showNotes ? (
            <ChatAnnouncementBar
              announcement={latestAnnouncement}
              count={announcementCount}
              onOpenList={() => setIsAnnouncementListOpen(true)}
            />
          ) : null}

          {/* 共通D&Dオーバーレイ */}
          <ChatDragOverlay
            show={isDragActive && !isModalOpen}
            rect={overlayRect}
            title="ここにドロップで添付"
            sub="画像（最大5枚）またはファイル1点（100MBまで）"
          />

          {/* 応答バナー */}
          {!showNotes && showNewMsgBanner && (
            <div
              className="new-message-banner"
              onClick={handleBannerClick}
            >
              <i className="fa-solid fa-comment" style={{ fontSize: 18, marginRight: 10, color: "var(--artist-chat-banner-icon)" }}></i>
              <span style={{ fontWeight: 600, color: "var(--event-chat-banner-text)", fontSize: 14 }}>
                新着メッセージが届きました
              </span>
            </div>
          )}

          {!showNotes && ((showDelayedInitialLoading && !shouldShowChatGuidance) || isUploading) && (
            <div className="chat-loading-overlay" aria-label={isUploading ? '送信中' : '読み込み中'}>
              <div className="chat-spinner" aria-hidden="true"></div>
            </div>
          )}

          {showNotes ? (
            <ArtistPersonalNotesPanel
              artistId={artistUser?.userId}
              onModeChange={() => {}}
              onNotesChanged={refreshNoteCount}
            />
          ) : (
          <div className="artist-message-chat-messages chat-message-stack" ref={chatContainerRef}>
            {withUnreadDivider(displayMessages.map((message) => {
              const isAdmin = message.message_type === 'admin_message';
              const isSystem = isSystemFormat(message.message_format);
              const isNoteMessage = isGroupNoteMessage(message);
              const presentation = buildArtistIndividualMessagePresentation({
                message,
                isSystemMessage: isSystem,
                resolveAdminName: getAdminDisplayName,
              });
              const displayName = presentation.leftSenderName;
              const senderContent = presentation.isAdminMessage && displayName ? <AdminSenderName name={displayName} /> : displayName;

              // ★ created_at はユーティリティでJST整形（Z付きもTZ無しも安全）
              const timeText = formatDateTimeJST(message.created_at);
              const flyerCacheKey = String(message.id || `${message.message_format}-${message.created_at}`);
              const cachedFlyerUrl = getCachedFlyerUrl(flyerCacheKey);
              const showCardIcon = false;
              const systemToneClass = getSystemToneClass(message);
              const isSelfRow = presentation.isSelfRow;
              const readCount = !isAdmin && message.is_read ? 1 : 0;
              const normalizedMessageId = String(message?.id || '').trim();
              const hasCopyFocus = Boolean(
                normalizedMessageId &&
                inlineCopyMessageId &&
                inlineCopyMessageId === normalizedMessageId
              );
              const shouldDimRow = Boolean(
                inlineCopyMessageId &&
                normalizedMessageId &&
                inlineCopyMessageId !== normalizedMessageId
              );
              if (isNoteMessage) {
                const noteAuthorIsAdmin = isAdmin || String(message.sender_type || '').toLowerCase() === 'admin';
                const noteAuthorName = noteAuthorIsAdmin
                  ? getAdminDisplayName(message)
                  : (artistUser?.name || message.artist_name || 'あなた');
                const artistInitial = String(noteAuthorName || 'ア').trim().charAt(0) || 'ア';
                const noteArtistAvatarNode = !noteAuthorIsAdmin ? (
                  <span className="chat-avatar" aria-hidden="true">
                    <span className="chat-avatar-initial">{artistInitial}</span>
                  </span>
                ) : null;
                return (
                  <ChatNoteSystemMessageRow
                    key={message.id}
                    message={message}
                    variant="im"
                    preset="artistIndividual"
                    avatarNode={noteAuthorIsAdmin ? renderAdminAvatar(message) : noteArtistAvatarNode}
                    senderName={noteAuthorName}
                    senderContent={noteAuthorIsAdmin ? <AdminSenderName name={noteAuthorName} /> : noteAuthorName}
                    authorIsAdmin={noteAuthorIsAdmin}
                    bubbleClassName="admin-message system-message"
                    timeLabel={timeText}
                    activeAnchorMessageId={activeAnchorMessageId}
                    onOpenNote={(noteId) => {
                      setShowNotes(true);
                      if (noteId) {
                        window.setTimeout(() => {
                          window.dispatchEvent(
                            new CustomEvent('artist-personal-notes-open-detail', { detail: { noteId } })
                          );
                        }, 0);
                      }
                    }}
                  />
                );
              }
              const renderedSystemMessage = renderIndividualSystemMessage({
                message,
                isSystemMessage: isSystem,
                showCardIcon,
                systemToneClass,
                formatMessageContent,
                parseReminderContent,
                cachedFlyerUrl,
                buildFlyerUrl,
                pickFlyerFilename,
                resolveGenreBadge: (genre) => {
                  const primaryGenre = resolvePrimaryGenreObj(genre);
                  if (!primaryGenre) return null;
                  return {
                    label: primaryGenre.label,
                    backgroundColor: primaryGenre.color || 'var(--event-chat-action-blue)',
                    color: primaryGenre.text_color || 'var(--color-inverse)',
                  };
                },
                formatEventDate: toYmdSlash,
                formatEventTime: toHHmm,
                titles: {
                  partNotice: 'お知らせが届きました',
                  stageSeatingShared: 'ステージシーティングが共有されました',
                  hold: 'オファーが届きました',
                  holdCancelled: 'オファーがキャンセルされました',
                  contract: '本契約が届きました',
                },
                links: {
                  contractDetailHref: (msg) =>
                    msg?.contract_artist_id
                      ? `/artist/workspaces/contracts/${msg.contract_artist_id}`
                      : '',
                  holdDetailHref: (msg) =>
                    msg?.hold_casting_id ? `/artist/workspaces/casting/offers/${msg.hold_casting_id}` : '',
                  eventDetailHref: (eventUuid) =>
                    eventUuid ? `/artist/workspaces/event-operations/events/${eventUuid}` : '',
                  holdDeclineHref: getHoldDeclineLink,
                  conciergeFallbackHref: '/artist/workspaces/concierge/dashboard',
                },
                extractFirstUrl,
                hideUnsupportedSystemMessages: true,
              });

              if (isSystem && renderedSystemMessage == null) {
                return null;
              }
              const avatarNode = renderedSystemMessage ? (
                <ChatAnnouncementAvatar
                  toneClassName={getSystemAvatarToneClass(message)}
                  iconClass={getSystemAvatarIconClass(message)}
                />
              ) : isAdmin ? renderAdminAvatar(message) : null;

              return (
                <IndividualStandardMessageRow
                  key={message.id}
                  message={message}
                  variant="im"
                  preset="artistIndividual"
                  isSelf={isSelfRow}
                  senderName={displayName}
                  senderContent={senderContent}
                  showSender={Boolean(displayName)}
                  readCount={readCount}
                  readLabel="既読"
                  timeLabel={timeText}
                  bubbleClassName={`${message.className || ''}${hasCopyFocus ? ' chat-message-row__bubbleWrap--copyActive' : ''}`}
                  metaExtra={
                    renderedSystemMessage && shouldShowAdminSenderForMessage(message)
                      ? renderAdminSenderMeta(message)
                      : null
                  }
                  rowClassName={
                    hasCopyFocus
                      ? 'chat-message-row--copyActive'
                      : shouldDimRow
                        ? 'chat-message-row--copyDimmed'
                        : ''
                  }
                  activeAnchorMessageId={activeAnchorMessageId}
                  avatarNode={avatarNode}
                  onContextMenu={inlineCopyMessageId ? undefined : (e) => handleMessageContextMenu(e, message)}
                  onTouchStart={inlineCopyMessageId ? undefined : (e) => handleMessageTouchStart(e, message)}
                  onTouchEnd={inlineCopyMessageId ? undefined : clearMessageLongPress}
                  onTouchCancel={inlineCopyMessageId ? undefined : clearMessageLongPress}
                  onTouchMove={inlineCopyMessageId ? undefined : clearMessageLongPress}
                  renderMessageContent={() =>
                    renderPartialCopyContent(
                      message,
                      renderedSystemMessage ?? (
                        (() => {
                          const fileName = message.original_name || '';
                          const resolvedImage =
                            message.message_format === ADMIN_MESSAGE_FORMATS.IMAGE
                              ? resolveIndexedImagePreview({
                                  message,
                                  imageIndexByMessageId,
                                  imageMessages,
                                  imagePreviewUrls,
                                  buildPreviewUrl: (name) => buildMessageImageUrl(name, 'proxy'),
                                })
                              : { safeIndex: -1, previewSrc: '' };

                          return renderIndividualStandardMessageContent({
                            message,
                            replyContent: message.reply ? (
                              <ChatReplySnippet
                                reply={message.reply}
                                onClick={() => handleJumpToReplyMessage(message.reply)}
                              />
                            ) : null,
                            formatMessageContent,
                            formatExpireDateLabel: formatDateJPFromAny,
                            fileName,
                            fileUrl: buildArtistMessageFileDownloadUrl(message.file_id),
                            canDownloadFile: !!fileName,
                            onDownloadFile: () => handleDownload('file', message.file_id, fileName),
                            imagePreviewSrc: resolvedImage.previewSrc,
                            onOpenImage: () => handleImageClick(resolvedImage.safeIndex),
                            onDownloadImage: () => handleDownload('image', message.image_id),
                            imageGroupItems: message.image_group_items,
                            onMentionClick: handleMentionClick,
                            buildImageGroupPreviewItem: (item) => {
                              const indexed = resolveIndexedImagePreview({
                                message: item,
                                imageIndexByMessageId,
                                imageMessages,
                                imagePreviewUrls,
                                buildPreviewUrl: (name) => buildMessageImageUrl(name, 'proxy'),
                              });
                              return {
                                messageId: item.id,
                                imageId: item.image_id,
                                src: indexed.previewSrc,
                                width: item.image_width,
                                height: item.image_height,
                                modalIndex: indexed.safeIndex,
                              };
                            },
                            onOpenImageGroupItem: (item) => handleImageClick(item.modalIndex),
                          });
                        })()
                      ),
                      isSelfRow
                    )
                  }
                  sideActionContent={
                    !inlineCopyMessageId &&
                    !renderedSystemMessage && !isSelfRow && supportsChatMessageReaction(message) ? (
                      <ChatMessageReactionBar
                        messageId={message.id}
                        reactions={message.reactions}
                        canReact
                        showReactionChips={false}
                        onToggleReaction={toggleReaction}
                        onOpenDetail={openReactionDetail}
                        isPending={isReactionPending}
                        compact
                      />
                    ) : null
                  }
                  metaEndContent={
                    !renderedSystemMessage &&
                    Array.isArray(message.reactions) &&
                    message.reactions.length > 0 ? (
                      <ChatMessageReactionBar
                        messageId={message.id}
                        reactions={message.reactions}
                        canReact={false}
                        onOpenDetail={openReactionDetail}
                        isPending={isReactionPending}
                        showAddButton={false}
                      />
                    ) : null
                  }
                />
              );
            }), displayMessages, unreadDividerMessageId)}
            {loadingOlderMessages && hasMore && (
              <div className="chat-paging-loading" aria-label="追加読み込み中">
                <div className="chat-spinner chat-spinner--small" aria-hidden="true"></div>
              </div>
            )}
            {shouldShowChatGuidance ? <ArtistTacticartChatGuidanceMessage /> : null}
          </div>
          )}

          {!showNotes ? (
          <ChatComposer
            mode="artist"
            scope="individual"
            canSendFiles={true}
            placeholderText="メッセージを入力…"
            onUploading={handleUploading}
            replyDraft={replyDraft}
            onReplyCancel={() => setReplyDraft(null)}
              onReplyChange={setReplyDraft}
            mentionCandidates={personalMentionCandidates}
            mentionHelperText="最初の一覧にいないスタッフを探すには、「@」のあとに名前を続けて入力してください（例：@佐藤）。"
            onMentionQueryChange={handlePersonalMentionQueryChange}
            initialDraft={quickTaskInitialMentionDraft}
            readOnly={isReadOnly}
            onSendBlocked={(msg) => {
              if (toastError) {
                toastError(msg || '現在メンテナンス中のため送信できません。');
              }
            }}
            onSent={async () => {
              // 送信後は最新へ反映＆上端へ
              await fetchInitialMessages(true);
              refreshPendingMentionTargets();
              setReplyDraft(null);
              requestAnimationFrame(() => {
                scrollToLatest();
              });
            }}
          />
          ) : null}
        </div>

        <aside
          className={`chat-side-panel side-panel artist-chat-side-panel${
            isSidePanelOpen ? '' : ' chat-side-panel--collapsed'
          }`}
        >
          {isSidePanelOpen ? (
            <>
              <ChatSidePanelHeader
                icon="fa-user"
                label="担当者情報"
                onCollapse={() => setIsSidePanelOpen(false)}
              />
              <ChatSidePanelCard
                title="参加スタッフ"
                headExtra={(
                  <ExplanationButton
                    title="参加スタッフの表示条件"
                    ariaLabel="参加スタッフの表示条件を確認"
                    body="通常は、このチャットでメッセージを送ったスタッフを直近順に20名まで表示します。メンション検索中は、名前で見つけたスタッフも加わります。"
                  />
                )}
              >
                <ChatSidePanelSection
                  title=""
                  className=""
                  isEmpty={adminParticipants.length === 0}
                  emptyText="まだスタッフの参加情報がありません。"
                >
                  <ChatSidePanelPeopleList
                    people={adminParticipants}
                    onSelect={openAdminProfile}
                    resolveSelectPayload={(adm) => adm?.source || adm}
                    resolveRowKey={(adm) => adm?.id}
                    resolveRowProps={(adm) => {
                      const displayName = adm?.fullName || '';
                      const avatarUrl = adm?.picture
                        ? appendVariant(buildAssetUrl(ADMIN_PROFILE_IMAGE_PATH, adm.picture), 'medium')
                        : '';
                      const roleMeta = [adm?.position, adm?.department].filter(Boolean).join(' / ');
                      return {
                        adminUserId: adm?.adminUserId ?? adm?.admin_user_id ?? adm?.user_id ?? adm?.id,
                        rowClassName: 'side-panel-profile side-panel-profile--clickable',
                        avatarNode: (
                          <PersonAvatar
                            role="admin"
                            name={displayName || 'スタッフ'}
                            imageUrl={avatarUrl}
                            size={44}
                            ariaHidden
                          />
                        ),
                        nickname: adm?.nickname || '',
                        name: displayName || 'スタッフ',
                        meta: roleMeta,
                        secondaryMeta: adm?.email || '',
                        showMeta: Boolean(roleMeta || adm?.email),
                        metaClassName: 'side-meta side-meta--role',
                        metaClassNameSecondary: 'side-meta',
                        fallbackName: 'スタッフ',
                      };
                    }}
                    listClassName="side-list"
                  />
                </ChatSidePanelSection>
              </ChatSidePanelCard>

              <AnnouncementSummarySidePanelCard
                title="オファー"
                rows={announcementSummaries.offers}
                emptyText="まだオファーの履歴がありません。"
                formatDate={formatDateJPFromAny}
              />

              <AnnouncementSummarySidePanelCard
                title="本契約"
                rows={announcementSummaries.contracts}
                emptyText="まだ本契約の履歴がありません。"
                formatDate={formatDateJPFromAny}
              />
            </>
          ) : (
            <div className="chat-side-panel__collapsed-rail">
              <ChatSidePanelToggleButton
                isOpen={false}
                onToggle={() => setIsSidePanelOpen(true)}
                label="担当者情報"
                iconOnly
                className="chat-side-panel__collapsed-toggle"
              />
            </div>
          )}
        </aside>
        </>
        )}
        </ChatWorkspaceLayout>

        {!isGroupTarget ? (
        <>
        <ChatImageModal
          isOpen={isModalOpen}
          src={imageFullUrls[currentImageIndex]}
          downloadItems={imageModalItems}
          currentIndex={currentImageIndex}
          onClose={closeModal}
          closeOnBack={false}
          onPrev={goOlder}
          onNext={goNewer}
          showNav={imageFullUrls.length > 1}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        />

        <ChatProfileModalLayer
          profileTarget={profileTarget}
          onClose={closeProfileModal}
          variant="artist"
          allowArtistLink={false}
        />

        {messageMenu && (
          <div className="message-menu-overlay" onClick={closeMessageMenu}>
            <div
              ref={messageMenuRef}
              className="message-menu"
              style={{ top: messageMenu.y, left: messageMenu.x }}
              onClick={(e) => e.stopPropagation()}
            >
              {canReactMessage(messageMenu.message) ? (
                <ChatMessageMenuReactionPicker
                  messageId={messageMenu.message?.id}
                  reactions={messageMenu.message?.reactions}
                  onSelectReaction={handleMessageMenuReactionSelect}
                  isPending={isReactionPending}
                />
              ) : null}
                {canReplyMessage(messageMenu.message) ? (
                  <button
                    type="button"
                    className="message-menu__item"
                    onClick={handleSelectReplyMessage}
                  >
                    <i className="fa-solid fa-reply" aria-hidden="true" />
                    <span>返信する</span>
                  </button>
                ) : null}
                {canCopyMessage(messageMenu.message) ? (
                  <button
                    type="button"
                    className="message-menu__item"
                    onClick={handleCopyMessage}
                  >
                    <i className="fa-solid fa-copy" aria-hidden="true" />
                    <span>コピー</span>
                  </button>
                ) : null}
                {canCopyMessage(messageMenu.message) ? (
                <button
                  type="button"
                  className="message-menu__item"
                  onClick={handleOpenInlinePartialCopy}
                >
                  <i className="fa-solid fa-i-cursor" aria-hidden="true" />
                  <span>一部をコピー</span>
                </button>
              ) : null}
                {canAnnouncementMessage(messageMenu.message) ? (
                  <button
                    type="button"
                    className="message-menu__item"
                    onClick={handleToggleAnnouncementMessage}
                  >
                    <i
                      className={`fa-solid ${
                        announcementActionMessageId === String(messageMenu.message?.id || '')
                          ? 'fa-spinner fa-spin'
                          : isAnnouncedMessage(messageMenu.message?.id)
                            ? 'fa-bullhorn-slash'
                            : 'fa-bullhorn'
                      }`}
                      aria-hidden="true"
                    />
                    <span>
                      {announcementActionMessageId === String(messageMenu.message?.id || '')
                        ? '処理中…'
                        : isAnnouncedMessage(messageMenu.message?.id)
                          ? 'アナウンス解除'
                          : 'アナウンスに設定'}
                    </span>
                  </button>
                ) : null}
              {canUnsendMessage(messageMenu.message) ? (
                <button
                  type="button"
                  className="message-menu__item message-menu__item--danger"
                  onClick={handleUnsendMessage}
                >
                  送信取り消し
                </button>
              ) : null}
            </div>
          </div>
        )}

        <ChatAnnouncementListModal
        open={isAnnouncementListOpen}
        announcements={announcements}
        onClose={() => setIsAnnouncementListOpen(false)}
        onRemove={unpinAnnouncement}
        removingMessageId={announcementActionMessageId}
        onSelect={handleSelectAnnouncementMessage}
      />
      <ChatMessageReactionDetailModal
        open={isReactionDetailOpen}
        loading={reactionDetailLoading}
        groups={reactionDetailGroups}
        onClose={closeReactionDetail}
      />
      <ChatMentionTaskResolutionModal {...mentionResolutionModalProps} />

      <ChatLinksModal
        isOpen={showLinksModal}
        loadLinks={ARTIST_PERSONAL_ASSET_SOURCE.loadLinks}
        onClose={() => setShowLinksModal(false)}
      />
      <ChatMediaModal
        isOpen={showMediaModal}
        loadMedia={ARTIST_PERSONAL_ASSET_SOURCE.loadMedia}
        downloadZip={ARTIST_PERSONAL_ASSET_SOURCE.downloadZip}
        buildFileUrl={ARTIST_PERSONAL_ASSET_SOURCE.buildFileUrl}
        onClose={() => setShowMediaModal(false)}
      />
        </>
        ) : null}
      </div>
    </div>);
}
